import React, { useState } from 'react';
import Avatar from 'react-avatar';
import {
    MdPerson,
    MdEdit,
    MdVisibility,
    MdArrowUpward,
    MdArrowDownward,
    MdCheck,
    MdClose,
    MdStars
} from 'react-icons/md';
import './ParticipantsPanel.css';

const ROLE_META = {
    owner:  { icon: <MdPerson size={14} />, label: 'Owner',  cls: 'role-owner'  },
    editor: { icon: <MdEdit size={14} />, label: 'Editor', cls: 'role-editor' },
    viewer: { icon: <MdVisibility size={14} />, label: 'Viewer', cls: 'role-viewer' },
};

/**
 * ParticipantsPanel
 *
 * Displays all participants with their role badges.
 * When the current user is the owner, action buttons appear for each
 * non-owner participant: Promote/Demote, Transfer Ownership, Kick.
 *
 * Props:
 *   participants  – [{ socketId, username, role, userId }]
 *   mySocketId    – string
 *   myRole        – 'owner' | 'editor' | 'viewer'
 *   onChangeRole  – (targetSocketId, newRole) => void
 *   onKickUser    – (targetSocketId) => void
 *   onTransferOwnership – (targetSocketId) => void
 */
const ParticipantsPanel = ({
    participants = [],
    mySocketId,
    myRole,
    onChangeRole,
    onKickUser,
    onTransferOwnership,
}) => {
    const [confirmTransfer, setConfirmTransfer] = useState(null); // socketId of transfer target
    const [confirmKick, setConfirmKick] = useState(null);         // socketId of kick target

    const isOwner = myRole === 'owner';

    const handleRoleToggle = (p) => {
        const newRole = p.role === 'editor' ? 'viewer' : 'editor';
        onChangeRole(p.socketId, newRole);
    };

    // Sort: owner first, then editors, then viewers
    const roleOrder = { owner: 0, editor: 1, viewer: 2 };
    const sorted = [...participants].sort((a, b) =>
        (roleOrder[a.role] ?? 3) - (roleOrder[b.role] ?? 3)
    );

    return (
        <div className="participantsPanel">
            <div className="participantsPanelHeader">
                <span className="participantsPanelTitle">Participants</span>
                <span className="participantsCount">{participants.length}</span>
            </div>

            <div className="participantsList">
                {sorted.map((p) => {
                    const isMe = p.socketId === mySocketId;
                    const meta = ROLE_META[p.role] || ROLE_META.viewer;
                    const canManage = isOwner && !isMe && p.role !== 'owner';

                    return (
                        <div
                            key={p.socketId}
                            className={`participantItem ${isMe ? 'participantItemMe' : ''}`}
                        >
                            {/* Left: avatar + name + role badge */}
                            <div className="participantLeft">
                                <Avatar name={p.username} size={30} round="6px" />
                                <div className="participantInfo">
                                    <span className="participantName">
                                        {p.username}
                                        {isMe && (
                                            <span className="participantYouTag"> (you)</span>
                                        )}
                                    </span>
                                    <span className={`participantRoleBadge ${meta.cls}`} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        {meta.icon}
                                        <span>{meta.label}</span>
                                    </span>
                                </div>
                            </div>

                            {/* Right: owner actions */}
                            {canManage && (
                                <div className="participantActions">
                                    {/* Promote / Demote */}
                                    <button
                                        className="pActionBtn pPromoteBtn"
                                        title={
                                            p.role === 'viewer'
                                                ? 'Promote to Editor'
                                                : 'Demote to Viewer'
                                        }
                                        onClick={() => handleRoleToggle(p)}
                                    >
                                        {p.role === 'viewer' ? <MdArrowUpward size={16} /> : <MdArrowDownward size={16} />}
                                    </button>

                                    {/* Transfer Ownership — two-step confirm */}
                                    {confirmTransfer === p.socketId ? (
                                        <div className="pConfirmRow">
                                            <span className="pConfirmText">Transfer?</span>
                                            <button
                                                className="pActionBtn pConfirmYes"
                                                onClick={() => {
                                                    onTransferOwnership(p.socketId);
                                                    setConfirmTransfer(null);
                                                }}
                                            >
                                                <MdCheck size={16} />
                                            </button>
                                            <button
                                                className="pActionBtn pConfirmNo"
                                                onClick={() => setConfirmTransfer(null)}
                                            >
                                                <MdClose size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            className="pActionBtn pCrownBtn"
                                            title="Transfer Ownership"
                                            onClick={() => {
                                                setConfirmKick(null);
                                                setConfirmTransfer(p.socketId);
                                            }}
                                        >
                                            <MdStars size={16} />
                                        </button>
                                    )}

                                    {/* Kick — two-step confirm */}
                                    {confirmKick === p.socketId ? (
                                        <div className="pConfirmRow">
                                            <span className="pConfirmText">Remove?</span>
                                            <button
                                                className="pActionBtn pConfirmYes"
                                                onClick={() => {
                                                    onKickUser(p.socketId);
                                                    setConfirmKick(null);
                                                }}
                                            >
                                                <MdCheck size={16} />
                                            </button>
                                            <button
                                                className="pActionBtn pConfirmNo"
                                                onClick={() => setConfirmKick(null)}
                                            >
                                                <MdClose size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            className="pActionBtn pKickBtn"
                                            title="Remove from room"
                                            onClick={() => {
                                                setConfirmTransfer(null);
                                                setConfirmKick(p.socketId);
                                            }}
                                        >
                                            <MdClose size={16} />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {participants.length === 0 && (
                    <div className="participantsEmpty">No participants yet.</div>
                )}
            </div>
        </div>
    );
};

export default ParticipantsPanel;
